import { useState } from "react";
import { API, downloadFile } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function CalendarGeneratorPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("mon");
  const [busy, setBusy] = useState(false);

  async function generate() {
    setBusy(true);
    try {
      const res = await fetch(`${API}/tools/calendar-generator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month: Number(month), year: Number(year), title, start_weekday: start }),
      });
      if (!res.ok) throw new Error("gen failed");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href; a.download = `calendar_${year}_${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Could not generate");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="animate-fade-up max-w-3xl">
      <div className="mb-8">
        <span className="label-caps">Tool</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Calendar Generator.</h1>
        <p className="mt-2 text-[color:var(--text-muted)]">Printable monthly calendar as PDF, with room to write.</p>
      </div>

      <div className="card-tactile rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-caps mb-2 block">Month</label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="cg-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-caps mb-2 block">Year</label>
            <Input type="number" value={year} min="1900" max="2100" onChange={(e) => setYear(Number(e.target.value))} data-testid="cg-year" />
          </div>
        </div>
        <div>
          <label className="label-caps mb-2 block">Title (optional)</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Family Plan" data-testid="cg-title" />
        </div>
        <div>
          <label className="label-caps mb-2 block">Week starts on</label>
          <Select value={start} onValueChange={setStart}>
            <SelectTrigger data-testid="cg-start" className="max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mon">Monday</SelectItem>
              <SelectItem value="sun">Sunday</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={generate} disabled={busy} data-testid="cg-generate" className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white">
          <FileDown className="h-4 w-4 mr-2" /> {busy ? "Generating…" : "Download PDF"}
        </Button>
      </div>
    </div>
  );
}
